import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
const { FiServer, FiCheckCircle, FiActivity, FiAlertCircle, FiRefreshCw, FiLoader, FiSend, FiInfo, FiDatabase, FiLayers, FiPlus, FiX } = FiIcons;
import { mockProviders } from '../data/mockData';

const showToast = (message, type = 'success') => {
  window.dispatchEvent(new CustomEvent('show-toast', { detail: { message, type } }));
};

const CreateProviderModal = ({ onClose, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    provider_name: '',
    provider_type: 'email',
    status: 'active',
    priority: 1,
    daily_limit: 1000,
    monthly_limit: 10000,
    notes: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSave(formData);
      onClose();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <form onSubmit={handleSubmit} className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        <div className="px-6 py-4 bg-slate-900 text-white flex justify-between items-center">
          <h3 className="font-bold flex items-center gap-2"><SafeIcon icon={FiPlus} /> Add New Provider (PB)</h3>
          <button type="button" onClick={onClose} className="p-1 hover:bg-slate-800 rounded"><SafeIcon icon={FiX} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Provider Name</label>
            <input required type="text" className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold" placeholder="e.g. Emailit Production" value={formData.provider_name} onChange={e => setFormData({ ...formData, provider_name: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Type</label>
              <select className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm" value={formData.provider_type} onChange={e => setFormData({ ...formData, provider_type: e.target.value })}>
                <option value="email">Email (SMTP/API)</option>
                <option value="sms">SMS (Coming Soon)</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Priority</label>
              <input type="number" className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm" value={formData.priority} onChange={e => setFormData({ ...formData, priority: parseInt(e.target.value) })} />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Internal Notes</label>
            <textarea className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs h-20 resize-none" placeholder="Integration details..." value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} />
          </div>
        </div>
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-bold text-slate-500">Cancel</button>
          <button type="submit" disabled={loading} className="px-6 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold shadow-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
            {loading && <SafeIcon icon={FiLoader} className="animate-spin" />} Register Provider
          </button>
        </div>
      </form>
    </div>
  );
};

const ProviderCard = ({ provider, onTestSend }) => {
  const [testTo, setTestTo] = useState('');
  const [showTestForm, setShowTestForm] = useState(false);
  const [sending, setSending] = useState(false);
  
  const isEmailit = provider.name.toLowerCase().includes('emailit');
  const isPrimary = provider.mode === 'Primary' || provider.priority === 1;

  const handleTest = async () => {
    if (!testTo) return showToast('Please enter a recipient email', 'error');
    setSending(true);
    try {
      await onTestSend(testTo);
      setShowTestForm(false);
      setTestTo('');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={`bg-white rounded-2xl border-2 transition-all duration-300 ${isPrimary ? 'border-blue-500 shadow-md ring-4 ring-blue-500/5' : 'border-slate-200 hover:border-slate-300'}`}>
      <div className="p-5 border-b border-slate-100 min-h-[160px]">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${isPrimary ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'}`}>
              <SafeIcon icon={FiServer} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-800">{provider.name}</h3>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${provider.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'}`}>
                  {provider.status}
                </span>
              </div>
            </div>
          </div>
          <p className="text-[10px] font-black text-slate-400 uppercase">{provider.mode || (provider.priority === 1 ? 'Primary' : 'Standby')}</p>
        </div>
        
        {showTestForm && isEmailit ? (
          <div className="space-y-2 animate-in fade-in slide-in-from-top-2">
            <input 
              type="email" 
              placeholder="recipient@example.com" 
              className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs outline-none focus:border-blue-500"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
            />
            <div className="flex gap-2">
              <button 
                onClick={handleTest}
                disabled={sending}
                className="flex-1 py-1.5 bg-blue-600 text-white text-[10px] font-bold rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
              >
                {sending ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiSend} />}
                Send Test
              </button>
              <button onClick={() => setShowTestForm(false)} className="px-3 py-1.5 bg-slate-200 text-slate-600 text-[10px] font-bold rounded-lg hover:bg-slate-300">
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <p className="text-xs text-slate-500 leading-relaxed italic">
            {provider.notes || 'No configuration notes provided.'}
          </p>
        )}
      </div>
      <div className="p-4 grid grid-cols-2 gap-2 bg-slate-50/50 rounded-b-2xl">
        <button 
          onClick={() => isEmailit ? setShowTestForm(true) : showToast('Manual test for this provider not yet implemented', 'info')}
          className={`py-2 bg-white border border-slate-200 text-slate-600 rounded-lg text-[11px] font-bold transition-all hover:bg-white hover:border-blue-400 flex items-center justify-center gap-2`}
        >
          <SafeIcon icon={FiSend} /> Test Send
        </button>
        <button disabled className="py-2 bg-white border border-slate-200 text-slate-400 rounded-lg text-[11px] font-bold cursor-not-allowed flex items-center justify-center gap-2">
          <SafeIcon icon={FiActivity} /> Logs
        </button>
      </div>
    </div>
  );
};

const Providers = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLive, setIsLive] = useState(false);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const callGateway = async (action, payload = {}) => {
    try {
      const response = await fetch('/.netlify/functions/gateway', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, payload })
      });
      const text = await response.text();
      let result;
      try {
        result = JSON.parse(text);
      } catch (e) {
        console.error(`Gateway Debug [${action}]:`, { status: response.status, origin: window.location.origin });
        throw new Error(`Invalid JSON: ${response.status}`);
      }
      if (!result.success) throw new Error(result.message || 'Gateway Error');
      return result;
    } catch (err) {
      console.error(`Gateway Action [${action}] failed:`, err.message);
      throw err;
    }
  };

  const fetchProviders = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await callGateway('list_providers');
      const pbData = res.data;
      const mapped = (pbData || []).map(item => ({
        id: item.id,
        name: item.provider_name || item.name || 'Unknown Provider',
        status: item.status || 'ready',
        mode: item.mode,
        priority: item.priority,
        health: item.health_score || 100,
        notes: item.notes || '',
        isReal: true
      }));
      setProviders(mapped);
      setIsLive(true);
    } catch (err) {
      setError(`Backend error: ${err.message}`);
      setProviders(mockProviders.map(p => ({ ...p, isReal: false })));
      setIsLive(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProviders();
  }, []);

  const handleCreateProvider = async (formData) => {
    await callGateway('create_provider', formData);
    showToast('Provider registered in PocketBase');
    fetchProviders();
  };

  const handleTestEmailit = async (to) => {
    try {
      const res = await callGateway('test_emailit_send', {
        to,
        subject: 'BPA Gateway Emailit Test Send',
        html: `<div style="font-family: sans-serif; padding: 20px; border: 1px solid #e2e8f0; border-radius: 10px;">
                <h2 style="color: #2563eb;">BPA Gateway Test</h2>
                <p>This is a test email sent from the BPA Gateway via <b>Emailit</b>.</p>
                <p style="color: #64748b; font-size: 12px;">Timestamp: ${new Date().toLocaleString()}</p>
              </div>`,
        text: `BPA Gateway Test Send via Emailit at ${new Date().toLocaleString()}`
      });
      showToast(res.message);
    } catch (err) {
      showToast(err.message, 'error');
      console.error('Test Send Detailed Error:', err);
    }
  };

  return (
    <div className="space-y-6 animate-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Email Providers</h2>
          <div className="flex items-center gap-2 mt-1">
            {isLive ? (
              <span className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
                <SafeIcon icon={FiDatabase} /> LIVE VIA POCKETBASE
              </span>
            ) : (
              <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
                <SafeIcon icon={FiLayers} /> SHOWING SAMPLE DATA
              </span>
            )}
            {error && <span className="text-[10px] text-red-500 font-medium italic">({error})</span>}
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchProviders} className="p-2.5 bg-white border border-slate-200 text-slate-500 rounded-xl hover:bg-slate-50 transition-colors shadow-sm">
            <SafeIcon icon={FiRefreshCw} className={loading ? 'animate-spin' : ''} />
          </button>
          <button onClick={() => setShowAddModal(true)} className="px-5 py-2.5 bg-slate-900 text-white text-sm font-bold rounded-xl hover:bg-slate-800 transition-all shadow-md flex items-center gap-2">
            <SafeIcon icon={FiServer} /> Add New Provider
          </button>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-4 shadow-sm">
        <SafeIcon icon={FiInfo} className="text-blue-500 text-xl shrink-0 mt-0.5" />
        <div className="space-y-1">
          <p className="text-xs text-blue-800 leading-relaxed font-bold">
            Provider Test Mode (PB-07A)
          </p>
          <p className="text-xs text-blue-800/80 leading-relaxed">
            Click <b>Test Send</b> on an Emailit provider to verify your backend API key and sender configuration. This action bypasses API key validation and sends directly from the gateway server.
          </p>
        </div>
      </div>

      <div className="relative min-h-[200px]">
        {loading && (
          <div className="absolute inset-0 bg-slate-50/60 backdrop-blur-[1px] z-10 flex items-center justify-center">
            <SafeIcon icon={FiLoader} className="text-3xl text-blue-600 animate-spin" />
          </div>
        )}
        {isLive && providers.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-12 text-center space-y-4">
            <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto text-slate-400">
              <SafeIcon icon={FiServer} className="text-3xl" />
            </div>
            <div>
              <h3 className="font-bold text-slate-800">No Providers Configured</h3>
              <p className="text-sm text-slate-500 max-w-xs mx-auto mt-1 italic">
                Your live PocketBase collection is empty.
              </p>
              <button onClick={() => setShowAddModal(true)} className="mt-4 px-6 py-2 bg-slate-900 text-white rounded-xl text-xs font-bold">Register Provider</button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {providers.map(provider => (
              <ProviderCard key={provider.id} provider={provider} onTestSend={handleTestEmailit} />
            ))}
          </div>
        )}
      </div>

      {showAddModal && <CreateProviderModal onClose={() => setShowAddModal(false)} onSave={handleCreateProvider} />}
    </div>
  );
};

export default Providers;